import {WorkloadRequirement} from '@cquential/api';
import {PlanbordResource} from '@/types/api-object-extensions';

export default class WorkloadFulfilmentCalculator {
    private result: Record<number, Record<string, Record<number, number>>> = {};
    private hasRun: boolean = false;
    private placedResources: Record<number, true> = {};
    private readonly requirementsPerDayPerQuarterPerFunction: Record<string, Record<number, Record<number, WorkloadRequirement>>>;
    private readonly resourcesWorkingPerDayPerQuarter: Record<string, Record<number, PlanbordResource[]>>;

    constructor(
        requirementsPerDayPerQuarterPerFunction: Record<string, Record<number, Record<number, WorkloadRequirement>>>,
        resourcesWorkingPerDayPerQuarter: Record<string, Record<number, PlanbordResource[]>>,
    ) {
        this.requirementsPerDayPerQuarterPerFunction = requirementsPerDayPerQuarterPerFunction;
        this.resourcesWorkingPerDayPerQuarter = resourcesWorkingPerDayPerQuarter;
    }

    private run(): void {
        for (const day in this.requirementsPerDayPerQuarterPerFunction) {
            for (const time in this.requirementsPerDayPerQuarterPerFunction[day]) {
                this.runIteration(day, Number(time));
            }
        }
    }

    private runIteration(day: string, time: number): void {
        const resourcesPerFunctionOnThisTime: Record<number, PlanbordResource[]> = this.determineResourcesWorkingOnTime(day, time);
        const resourcesPerFunctionOnThisTimeSortedByAvailability: {functionId: number; resources: PlanbordResource[]}[] = this.sortResourcesBasedOnFunctionRarity(resourcesPerFunctionOnThisTime);
        this.placedResources = {};

        for (const functionIdAndResourceList of resourcesPerFunctionOnThisTimeSortedByAvailability) {
            this.tryToFulfillTarget(functionIdAndResourceList.resources, functionIdAndResourceList.functionId, day, time, this.requirementsPerDayPerQuarterPerFunction[day][time][functionIdAndResourceList.functionId].minimum);
        }
        for (const functionIdAndResourceList of resourcesPerFunctionOnThisTimeSortedByAvailability) {
            this.tryToFulfillTarget(functionIdAndResourceList.resources, functionIdAndResourceList.functionId, day, time, this.requirementsPerDayPerQuarterPerFunction[day][time][functionIdAndResourceList.functionId].optimum);
        }
        for (const functionIdAndResourceList of resourcesPerFunctionOnThisTimeSortedByAvailability) {
            this.tryToFulfillTarget(functionIdAndResourceList.resources, functionIdAndResourceList.functionId, day, time, this.requirementsPerDayPerQuarterPerFunction[day][time][functionIdAndResourceList.functionId].maximum);
        }
    }

    private determineResourcesWorkingOnTime(day: string, time: number): Record<number, PlanbordResource[]> {
        const resourcesWorkingOnThisTime: PlanbordResource[] = this.resourcesWorkingPerDayPerQuarter[day]?.[time] ?? [];
        const resourcesPerFunctionOnThisTime: Record<number, PlanbordResource[]> = {};

        if (0 in this.requirementsPerDayPerQuarterPerFunction[day][time]) {
            resourcesPerFunctionOnThisTime[0] = resourcesWorkingOnThisTime;
        }

        for (const resource of resourcesWorkingOnThisTime) {
            for (const resourceFunction of resource.functions!) {
                if (resourceFunction.id! in this.requirementsPerDayPerQuarterPerFunction[day][time]) {
                    resourcesPerFunctionOnThisTime[resourceFunction.id!] ??= [];
                    resourcesPerFunctionOnThisTime[resourceFunction.id!].push(resource);
                }
            }
        }

        return resourcesPerFunctionOnThisTime;
    }

    /**
     * Sort resources in order they should be assigned -> first assign the functions the least resources can fulfill, as common functions are much more likely to be fulfilled by other resources
     * This is done by the `.toSorted()` at the end of the function.
     *
     * Secondly, within each list of resources, sort them by how many functions they each can fulfill, so people with fewer functions are used first on their specific functions.
     * This is done by the `.toSorted()` of the resources, within the `.map()`-call.
     *
     * @param resourcesPerFunctionOnThisTime
     * @private
     */
    private sortResourcesBasedOnFunctionRarity(resourcesPerFunctionOnThisTime: Record<number, PlanbordResource[]>): {functionId: number; resources: PlanbordResource[]}[] {
        // possible optimization: when counting how many function each resource can fulfill: filter those lists based on what functions actually need be fulfilled
        // you could even weigh this by how many times those functions are needed - for now, this is overkill

        return Object
            .entries(resourcesPerFunctionOnThisTime)
            .map((resourcesOfFunctionOnThisTime: [string, PlanbordResource[]]): {functionId: number; resources: PlanbordResource[]} => ({
                functionId: Number(resourcesOfFunctionOnThisTime[0]),
                resources: resourcesOfFunctionOnThisTime[1].toSorted((a: PlanbordResource, b: PlanbordResource): number => a.functions!.length - b.functions!.length),
            }))
            .toSorted((a: {functionId: number; resources: PlanbordResource[]}, b: {functionId: number; resources: PlanbordResource[]}): number => a.resources.length - b.resources.length);
    }

    /**
     * Attempts to place up to `target` resources in the fulfilment of the given function, from the list of `resources`.
     */
    private tryToFulfillTarget(resources: PlanbordResource[], functionId: number, day: string, time: number, target: number): void {
        const initialCount: number = this.result[functionId]?.[day]?.[time] ?? 0;
        let count: number = initialCount;
        for (const resource of resources) {
            if (count === target) {
                break;
            }

            if (resource.id! in this.placedResources) {
                continue;
            }

            count++;
            this.placedResources[resource.id!] = true;
        }

        if (count > initialCount) {
            if (initialCount === 0) {
                this.result[functionId] ??= {};
                this.result[functionId][day] ??= {};
            }

            this.result[functionId][day][time] = count;
        }
    }

    public getResult(): Record<number, Record<string, Record<number, number>>> {
        if (!this.hasRun) {
            this.run();
            this.hasRun = true;
        }

        return this.result;
    }
}
